// Copied verbatim from eval/dashboard/internal_web/src/components/ui/InfoTip.tsx (ATM-57). Keep in step until ATM-60 shares it.
/**
 * A definition that opens on hover, hung off a small info button.
 *
 * Radix's HoverCard rather than the shared click `Popover`: a definition is not a
 * decision, and asking a reader to click — then click again to dismiss — for two
 * sentences they may want on four different tiles is friction the content has not
 * earned. HoverCard also opens on FOCUS, so the keyboard path this dashboard
 * insists on survives, and it does not pull focus into the panel the way a click
 * popover does — which on hover would rip the caret out from under the pointer.
 *
 * Not a native `title` and not Radix Tooltip either: the panel holds prose a
 * reviewer may want to re-read or select, and hoverable content is precisely what
 * HoverCard adds over Tooltip. One per metric rather than one per section — a
 * reviewer stops at the single number they distrust, and a paragraph covering five
 * metrics makes them read four definitions to find it.
 *
 * It carries definitions, never numbers. Anything scope-dependent belongs on the
 * face of the figure, where it re-renders when the scope changes; text in here
 * would quietly describe the scope the reader had two clicks ago.
 *
 * Drawn as the design has it: a grey `?` disc that turns dark on hover, opening a dark
 * tooltip with an arrow. The disc is two sizes — 20px on a stat tile, 16px beside a table
 * header or a chart's column heads.
 */

import * as HoverCard from "@radix-ui/react-hover-card";
import type { ReactNode } from "react";

import { cn } from "../../lib/cn";

/** Long enough that a pointer crossing the icon does not fire it. */
const OPEN_DELAY_MS = 120;

/** Grace period for the pointer to travel from the icon into the panel. */
const CLOSE_DELAY_MS = 140;

type Props = {
  /** Names both the button and the panel, e.g. `What "Coverage misses" means`. */
  label: string;
  /** Prose. Paragraphs are spaced by the panel, so pass plain `<p>` elements. */
  children: ReactNode;
  align?: "start" | "center" | "end";
  /** The disc: `md` on a stat tile, `sm` everywhere else. */
  size?: "sm" | "md";
  /** Positions the trigger button. The panel is portalled and takes none of it. */
  className?: string;
};

const DISC: Record<"sm" | "md", string> = {
  sm: "h-4 w-4 text-[10px]",
  md: "h-5 w-5 text-[12px]",
};

/** An info button that reveals a short definition of the thing beside it. */
export default function InfoTip({
  label,
  children,
  align = "start",
  size = "sm",
  className,
}: Props) {
  return (
    <HoverCard.Root openDelay={OPEN_DELAY_MS} closeDelay={CLOSE_DELAY_MS}>
      <HoverCard.Trigger asChild>
        {/* A real button, not a bare icon: hover is the fast path, but the panel has
            to be reachable by keyboard, and HoverCard opens on the trigger's focus. */}
        <button
          type="button"
          aria-label={label}
          className={cn(
            "inline-flex shrink-0 items-center justify-center rounded-full bg-meter-track font-medium leading-none text-ink-faint transition-colors",
            "hover:bg-tooltip hover:text-surface data-[state=open]:bg-tooltip data-[state=open]:text-surface",
            DISC[size],
            className,
          )}
        >
          <span aria-hidden>?</span>
        </button>
      </HoverCard.Trigger>
      <HoverCard.Portal>
        <HoverCard.Content
          aria-label={label}
          align={align}
          sideOffset={4}
          collisionPadding={12}
          className="z-50 w-[17rem] rounded-control bg-tooltip p-3 shadow-float"
        >
          <div className="flex flex-col gap-2 text-tip text-tooltip-ink">{children}</div>
          <HoverCard.Arrow className="fill-tooltip" width={16} height={6} />
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
}
